import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import * as React from 'react';
import { FlexBox } from '../box';
import { Button } from '../button';
import { Card, CardContent } from '../card';
import { Chip } from '../chip';
import { IconSync } from '../icon';
import { styled } from '../styled';
import { Typography } from '../typography';
import { makeEnum, withVersion } from '../utils';
const sentiments = [
    'default',
    'positive',
    'negative',
    'discovery',
    'neutral'
];
export const TrendCardSentiment = makeEnum(...sentiments);
const states = ['default', 'empty', 'loading', 'error'];
export const TrendCardState = makeEnum(...states);
const sizes = ['default'];
export const TrendCardSize = makeEnum(...sizes);
const PALE_BAR_OPACITY = 0.2;
/**
 * The Figma `<TrendCard>` sparkline binds each sentiment to a Material accent
 * `/500` primitive (`light-blue/500`, `light-green/500`, `red/500`,
 * `purple/500`, `blue-grey/500`). Those primitives are not all exposed as theme
 * tokens, so we map each sentiment to the closest semantic palette token instead
 * of importing raw MUI colors.
 *
 * `default` → `info.light` (`#03a9f4`) and `discovery` → `secondary.main`
 * (`#9c27b0`) resolve to the exact primitive hex. `positive` → `success.light`
 * and `negative` → `error.light` track the brighter `/500` primitives more
 * closely than the darker `.main` tokens. `neutral` → `text.secondary` is the
 * nearest blue-grey token (the `text` palette has no lighter variant).
 *
 * The pale (historical) bars are derived from the solid color via
 * {@link PALE_BAR_OPACITY}, mirroring the Figma `.../100` tint.
 */
const sentimentSolidColor = (theme, sentiment) => {
    switch (sentiment) {
        case 'positive':
            return theme.palette.success.light;
        case 'negative':
            return theme.palette.error.light;
        case 'discovery':
            return theme.palette.secondary.main;
        case 'neutral':
            return theme.palette.text.secondary;
        case 'default':
        default:
            return theme.palette.info.light;
    }
};
const sentimentBarColors = (theme, sentiment) => {
    const solid = sentimentSolidColor(theme, sentiment);
    return { solid, pale: alpha(solid, PALE_BAR_OPACITY) };
};
const BAR_COUNT = 7;
const BAR_AREA_HEIGHT = 5.5;
const BAR_MIN_HEIGHT = 2;
const BAR_DATA_MIN_HEIGHT = 4;
/** Fixed corner radius matching the Figma `border-radius-0,5` primitive (2px). */
const SPARKLINE_CORNER_RADIUS = 2;
const LOADING_BAR_HEIGHTS = [22, 27, 21, 30, 26, 32, 18];
const BAR_PULSE_STAGGER_MS = 300;
const LOADING_PULSE_DURATION = '4s';
/**
 * Pulse authored in Figma on the loading skeleton (`skel-value`), reused across
 * all skeleton blocks and the bars: opacity dips to 0.2 mid-cycle over the loop.
 */
const skeletonPulse = keyframes `
    0% {
        opacity: 1;
        animation-timing-function: linear;
    }
    8.52% {
        opacity: 1;
        animation-timing-function: cubic-bezier(0.4, 0, 1, 1);
    }
    47.77% {
        opacity: 0.2;
        animation-timing-function: cubic-bezier(0.4, 0, 1, 1);
    }
    72.77% {
        opacity: 1;
        animation-timing-function: linear;
    }
    100% {
        opacity: 1;
    }
`;
const StyledCard = styled(Card)({
    minWidth: 'min-content',
    width: '100%'
});
const StyledCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(2),
    '&:last-child': {
        paddingBottom: theme.spacing(2)
    }
}));
const Bar = styled(Typography)(({ $color, $height, theme }) => ({
    width: theme.spacing(1),
    height: $height,
    minHeight: BAR_MIN_HEIGHT,
    borderRadius: SPARKLINE_CORNER_RADIUS,
    backgroundColor: $color
}));
const SkeletonBar = styled(Typography)(({ $height, $delay, theme }) => ({
    width: theme.spacing(1),
    height: $height,
    minHeight: BAR_MIN_HEIGHT,
    borderRadius: SPARKLINE_CORNER_RADIUS,
    backgroundColor: theme.palette.grey[200],
    animation: `${skeletonPulse} ${LOADING_PULSE_DURATION} infinite`,
    animationDelay: `${$delay}ms`
}));
const SkeletonBlock = styled(Typography)(({ $width, $height, theme }) => ({
    display: 'block',
    width: $width,
    height: $height,
    borderRadius: SPARKLINE_CORNER_RADIUS,
    backgroundColor: theme.palette.grey[200]
}));
const TrendBars = ({ heights, sentiment }) => {
    const theme = useTheme();
    const { pale, solid } = sentimentBarColors(theme, sentiment);
    const barColor = (index) => index === heights.length - 1 ? solid : pale;
    return (_jsx(FlexBox, { "aria-hidden": "true", alignSelf: "center", "data-role": "trend-card-chart", gap: 0.25, height: theme.spacing(BAR_AREA_HEIGHT), vAlign: "flex-end", children: heights.map((height, index) => (_jsx(Bar, { component: "span", "$color": barColor(index), "$height": height }, index))) }));
};
const BarSkeleton = () => {
    const theme = useTheme();
    return (_jsx(FlexBox, { "aria-hidden": "true", alignSelf: "center", "data-role": "trend-card-chart", gap: 0.25, height: theme.spacing(BAR_AREA_HEIGHT), vAlign: "flex-end", children: LOADING_BAR_HEIGHTS.map((height, index) => (_jsx(SkeletonBar, { component: "span", "$delay": index * BAR_PULSE_STAGGER_MS, "$height": height }, index))) }));
};
const toBarHeights = (chartData, barAreaHeight) => {
    const displayedData = chartData.slice(-BAR_COUNT);
    const highestBar = Math.max(...displayedData, 0);
    return displayedData.map((value) => highestBar > 0
        ? Math.max(Math.round((value / highestBar) * barAreaHeight), BAR_DATA_MIN_HEIGHT)
        : BAR_MIN_HEIGHT);
};
const flatTickHeights = () => Array.from({ length: BAR_COUNT }, () => BAR_MIN_HEIGHT);
const isDefined = (value) => value !== undefined && value !== null;
const StatusTrendBars = ({ isLoading, isEmpty, isError, hasChart, sentiment, dataHeights }) => {
    if (isEmpty || isError) {
        return _jsx(TrendBars, { heights: flatTickHeights(), sentiment: sentiment });
    }
    if (isLoading) {
        return _jsx(BarSkeleton, {});
    }
    if (!hasChart) {
        return null;
    }
    return _jsx(TrendBars, { heights: dataHeights, sentiment: sentiment });
};
const TrendCardImpl = ({ label, metric, valueSlot, change, changeDescription, chartData = [], sentiment = 'default', state = 'default', size = 'default', emptyText = 'No events yet', errorText = "Couldn't load data", retryLabel = 'Retry', onRetry, ...cardProps }) => {
    const theme = useTheme();
    const isLoading = state === 'loading';
    const isEmpty = state === 'empty';
    const isError = state === 'error';
    const barAreaHeight = Number.parseFloat(theme.spacing(BAR_AREA_HEIGHT));
    const dataHeights = toBarHeights(chartData, barAreaHeight);
    const hasChart = dataHeights.length > 0;
    const renderLabel = () => {
        if (isLoading) {
            return (_jsx(FlexBox, { height: 20, vAlign: "center", children: _jsx(SkeletonBlock, { component: "span", "$height": 16, "$width": 96 }) }));
        }
        if (!isDefined(label)) {
            return null;
        }
        return (_jsx(Typography, { color: "text.secondary", variant: "detail", children: label }));
    };
    const renderValue = () => {
        if (isLoading) {
            return (_jsx(FlexBox, { height: 42, vAlign: "center", children: _jsx(SkeletonBlock, { component: "span", "$height": 32, "$width": 72 }) }));
        }
        if (isError) {
            return (_jsx(FlexBox, { height: 42, vAlign: "center", children: _jsx(Typography, { color: "text.primary", variant: "bodyEmphasis", children: errorText }) }));
        }
        const value = isEmpty ? (metric ?? 0) : metric;
        if (!isDefined(value)) {
            return null;
        }
        return (_jsxs(FlexBox, { gap: 0.5, vAlign: "center", children: [_jsx(Typography, { color: "text.primary", component: "h4", variant: "headingLarge", children: value }), isDefined(valueSlot) && valueSlot] }));
    };
    const renderTrendChange = () => {
        if (isLoading) {
            return (_jsx(FlexBox, { height: 30, vAlign: "center", children: _jsx(SkeletonBlock, { component: "span", "$height": 8, "$width": 48 }) }));
        }
        if (isError) {
            return (_jsx(FlexBox, { height: 30, vAlign: "center", children: _jsx(Button, { color: "primary", size: "small", startIcon: _jsx(IconSync, {}), variant: "outlined", onClick: onRetry, children: retryLabel }) }));
        }
        if (isEmpty) {
            return (_jsx(FlexBox, { height: 30, vAlign: "center", children: _jsx(Typography, { color: "text.secondary", variant: "detail", children: emptyText }) }));
        }
        const hasChange = isDefined(change);
        const hasChangeDescription = isDefined(changeDescription);
        if (!hasChange && !hasChangeDescription) {
            return null;
        }
        return (_jsxs(FlexBox, { flexWrap: "wrap", gap: 0.5, height: 30, vAlign: "center", children: [hasChange && (_jsx(Chip, { label: typeof change === 'number' ? String(change) : change })), hasChangeDescription && (_jsx(Typography, { color: "text.secondary", variant: "detail", children: changeDescription }))] }));
    };
    return (_jsx(StyledCard, { "data-size": size, "data-state": state, variant: "outlined", ...cardProps, children: _jsx(StyledCardContent, { children: _jsxs(FlexBox, { gap: 1, hAlign: "space-between", vAlign: "center", children: [_jsxs(FlexBox, { gap: 0.5, minWidth: 0, stack: true, stretch: true, children: [renderLabel(), renderValue(), renderTrendChange()] }), _jsx(StatusTrendBars, { dataHeights: dataHeights, hasChart: hasChart, isEmpty: isEmpty, isError: isError, isLoading: isLoading, sentiment: sentiment })] }) }) }));
};
export const TrendCard = withVersion(TrendCardImpl, '2.0.1');
